import {
  fullDateFormat,
  outputSameElseDateFormat,
  outputTimeFormat
} from 'constant/formats';
import type { Dayjs } from 'dayjs';
import { dayjs } from 'services/datesAndTimes';

import { ROUTES } from 'constant/routes';

import { HairStatusGroup, HairSystem } from 'types';

/** The three groups a client sees; Pending/Inactive stay front-desk only. */
export type HairListGroup =
  | HairStatusGroup.Available
  | HairStatusGroup.OnOrder
  | HairStatusGroup.Delivered;

export const HAIR_LIST_GROUPS: HairListGroup[] = [
  HairStatusGroup.Available,
  HairStatusGroup.OnOrder,
  HairStatusGroup.Delivered
];

export const HAIR_GROUP_LABELS: Record<HairListGroup, string> = {
  [HairStatusGroup.Available]: 'In Stock',
  [HairStatusGroup.OnOrder]: 'On Order',
  [HairStatusGroup.Delivered]: 'Delivered'
};

export const HAIR_SYSTEM_FALLBACK_TITLE = 'Hair System';

/**
 * One page on the My Hair page, and also what the dashboard tile asks for — using the same
 * size means both hit the same RTK Query cache entries.
 */
export const HAIR_SYSTEMS_PAGE_SIZE = 25;

/** `?status=` values on the My Hair page, so a tab survives refresh and can be linked to. */
export const HAIR_GROUP_SLUGS: Record<HairListGroup, string> = {
  [HairStatusGroup.Available]: 'in-stock',
  [HairStatusGroup.OnOrder]: 'on-order',
  [HairStatusGroup.Delivered]: 'delivered'
};

export const getHairGroupFromSlug = (slug?: string | null): HairListGroup =>
  HAIR_LIST_GROUPS.find((group) => HAIR_GROUP_SLUGS[group] === slug) ??
  HairStatusGroup.Available;

export const getHairGroupHref = (group: HairListGroup): string =>
  `${ROUTES.myHair}?status=${HAIR_GROUP_SLUGS[group]}`;

const HAIR_DATE_INPUT_FORMAT = 'YYYY-MM-DD';

const HAIR_TIME_INPUT_FORMATS = [
  'HH:mm:ss',
  'HH:mm',
  'h:mm A',
  'h:mm a',
  'hh:mm A',
  'hh:mm a'
] as const;

const trimOrEmpty = (value?: string | null): string =>
  typeof value === 'string' ? value.trim() : '';

/**
 * `get-cl-hair` answers with a bare array. Anything else (an error envelope, an object,
 * a string) is unusable and yields `null` so the query can report it as an error rather
 * than an empty list the client would read as "you have no hair systems".
 */
export const normalizeHairSystemsResponse = (
  data: unknown
): HairSystem[] | null => {
  if (!Array.isArray(data)) {
    return null;
  }

  return data.filter(
    (item): item is HairSystem =>
      Boolean(item) &&
      typeof item === 'object' &&
      typeof (item as HairSystem).o_hair_num === 'number'
  );
};

export const parseHairDate = (value?: string | null): Dayjs | null => {
  const trimmed = trimOrEmpty(value);

  if (!trimmed) {
    return null;
  }

  const strict = dayjs(trimmed, HAIR_DATE_INPUT_FORMAT, true);

  if (strict.isValid()) {
    return strict;
  }

  const loose = dayjs(trimmed);

  return loose.isValid() ? loose : null;
};

/**
 * The app's list date: `Sep 12` within the current year, `07/08/25` otherwise — the same
 * output as `formatCustomDate`, which My Purchases and the Forms tables use. "Today" is
 * resolved per call rather than at import, so the result can't go stale in a long session.
 */
export const formatHairDate = (value?: string | null): string => {
  const parsed = parseHairDate(value);

  if (!parsed) {
    return '';
  }

  return parsed.format(
    parsed.isSame(dayjs(), 'year') ? outputSameElseDateFormat : fullDateFormat
  );
};

/**
 * Appointment dates read like the rest of the app's appointments (`getHumanReadableDate`):
 * `Today`, `Tomorrow` and `Yesterday` for the days around now, otherwise `Sep 12` or
 * `07/08/25`.
 */
export const formatHairAppointmentDate = (value?: string | null): string => {
  const parsed = parseHairDate(value);

  if (!parsed) {
    return '';
  }

  const today = dayjs();

  if (parsed.isSame(today, 'day')) {
    return 'Today';
  }

  if (parsed.isSame(today.add(1, 'day'), 'day')) {
    return 'Tomorrow';
  }

  if (parsed.isSame(today.subtract(1, 'day'), 'day')) {
    return 'Yesterday';
  }

  return formatHairDate(value);
};

export const formatHairTime = (value?: string | null): string => {
  const trimmed = trimOrEmpty(value);

  if (!trimmed) {
    return '';
  }

  const matchedFormat = HAIR_TIME_INPUT_FORMATS.find((format) =>
    dayjs(trimmed, format, true).isValid()
  );

  return matchedFormat
    ? dayjs(trimmed, matchedFormat, true).format(outputTimeFormat)
    : trimmed;
};

export const getHairSystemTitle = (item: HairSystem): string =>
  trimOrEmpty(item.o_hr_type_name) ||
  trimOrEmpty(item.o_hr_type_category) ||
  HAIR_SYSTEM_FALLBACK_TITLE;

/** The category line, unless it merely repeats the title. */
export const getHairSystemSubtitle = (item: HairSystem): string => {
  const category = trimOrEmpty(item.o_hr_type_category);

  if (!category) {
    return '';
  }

  return category.toLowerCase() === getHairSystemTitle(item).toLowerCase()
    ? ''
    : category;
};

export const getHairSystemStatusLabel = (
  item: HairSystem,
  group: HairListGroup
): string => trimOrEmpty(item.o_status_name) || HAIR_GROUP_LABELS[group];

export const getHairSystemNumberLabel = (item: HairSystem): string =>
  `#${item.o_hair_num}`;

export interface HairSystemAppointment {
  /** `Today`, `Tomorrow`, `Yesterday`, `Sep 12` or `07/08/25`. */
  dateLabel: string;
  timeLabel: string;
  provider: string;
  isUpcoming: boolean;
}

export const getHairSystemAppointment = (
  item: HairSystem
): HairSystemAppointment | null => {
  const parsed = parseHairDate(item.o_apt_service_start_date);

  if (!parsed) {
    return null;
  }

  return {
    dateLabel: formatHairAppointmentDate(item.o_apt_service_start_date),
    timeLabel: formatHairTime(item.o_apt_service_start_tm),
    provider: trimOrEmpty(item.o_apt_empl_short),
    isUpcoming: !parsed.isBefore(dayjs(), 'day')
  };
};

/** `Today at 4:00 PM`, as the sidebar's Next Appointment reads; just the date without a time. */
export const formatHairAppointmentWhen = (
  appointment: HairSystemAppointment
): string =>
  appointment.timeLabel
    ? `${appointment.dateLabel} at ${appointment.timeLabel}`
    : appointment.dateLabel;

const RELATIVE_DAY_LABELS = ['Today', 'Tomorrow', 'Yesterday'];

/**
 * The one-line mention under a system: `Appointment today at 4:00 PM with Erica J`, or
 * `Appointment on Sep 30 at 4:00 PM` for a calendar date — the "on" phrasing the app uses
 * for dates in a sentence ("Issued on", "Next on"), and "with" for the provider as the
 * appointment lists do.
 */
export const formatHairAppointmentLine = (
  appointment: HairSystemAppointment
): string => {
  const when = formatHairAppointmentWhen(appointment);
  const line = RELATIVE_DAY_LABELS.includes(appointment.dateLabel)
    ? `Appointment ${when.charAt(0).toLowerCase()}${when.slice(1)}`
    : `Appointment on ${when}`;

  return appointment.provider ? `${line} with ${appointment.provider}` : line;
};

export interface HairSystemDetail {
  key: string;
  label: string;
  value: string;
  /** Already shown in the row's Ordered & Received column on desktop. */
  isDesktopColumn?: boolean;
}

/**
 * The expanded-row fields, in display order, keeping only the ones that carry a value — no
 * placeholders. Whatever the row itself shows is left out: name, category, status, hair #,
 * created date, and an upcoming appointment (the row has its own line for that). The
 * ordered/received dates are flagged, since only the desktop row has a column for them.
 */
export const getHairSystemDetails = (item: HairSystem): HairSystemDetail[] => {
  const appointment = getHairSystemAppointment(item);
  const appointmentValue =
    appointment && !appointment.isUpcoming
      ? [
          formatHairAppointmentWhen(appointment),
          appointment.provider && `with ${appointment.provider}`
        ]
          .filter(Boolean)
          .join(' ')
      : '';

  const details: HairSystemDetail[] = [
    {
      key: 'ordered',
      label: 'Ordered',
      value: formatHairDate(item.o_hair_dt_order),
      isDesktopColumn: true
    },
    {
      key: 'received',
      label: 'Received',
      value: formatHairDate(item.o_hair_dt_received),
      isDesktopColumn: true
    },
    {
      key: 'serial',
      label: 'Serial #',
      value: trimOrEmpty(item.o_hair_imprint)
    },
    { key: 'vendor', label: 'Vendor', value: trimOrEmpty(item.o_vendor_name) },
    { key: 'appointment', label: 'Appointment', value: appointmentValue }
  ];

  return details.filter((detail) => Boolean(detail.value));
};

export interface HairSystemMilestone {
  label: string;
  date: string;
}

/**
 * The date that matters most for where a system is right now: when it was ordered while
 * it's still with the vendor, and when it arrived once it's in stock or delivered. Falls
 * back to the created date for older records missing the specific one.
 */
export const getHairSystemMilestone = (
  item: HairSystem,
  group: HairListGroup
): HairSystemMilestone => {
  const ordered = formatHairDate(item.o_hair_dt_order);
  const received = formatHairDate(item.o_hair_dt_received);
  const created = formatHairDate(item.o_date);

  if (group === HairStatusGroup.OnOrder) {
    return ordered
      ? { label: 'Ordered', date: ordered }
      : { label: 'Created', date: created };
  }

  return received
    ? { label: 'Received', date: received }
    : { label: 'Created', date: created };
};

const getHairSortTimestamp = (item: HairSystem): number =>
  parseHairDate(item.o_date)?.valueOf() ?? 0;

/** Newest first, then by hair number so same-day systems keep a stable order. */
export const sortHairSystemsNewestFirst = (list: HairSystem[]): HairSystem[] =>
  [...list].sort(
    (left, right) =>
      getHairSortTimestamp(right) - getHairSortTimestamp(left) ||
      right.o_hair_num - left.o_hair_num
  );

export interface HairSystemWithGroup {
  item: HairSystem;
  group: HairListGroup;
}

/**
 * Merges per-group pages into one list: groups in the order given (the dashboard passes In
 * Stock first — ready now — then On Order), each newest first, keeping each system's group
 * so the row can label it. A system can only appear in one group; if the API ever repeats
 * one, the earlier group wins.
 */
export const mergeHairSystemGroups = (
  pages: Array<{ group: HairListGroup; list: HairSystem[] }>
): HairSystemWithGroup[] => {
  const seen = new Set<number>();

  return pages.flatMap(({ group, list }) =>
    sortHairSystemsNewestFirst(
      list.filter((item) => {
        if (seen.has(item.o_hair_num)) {
          return false;
        }

        seen.add(item.o_hair_num);

        return true;
      })
    ).map((item) => ({ item, group }))
  );
};
